import { createUseStyles } from 'react-jss';
import { useSubscription } from '@autopylot-internal/state';
import { useInstance } from '../../support/ioc/useInstance';
import { IonContent, IonModal } from '@ionic/react';
import { PageHeader } from '../../components/page/PageHeader.tsx';
import { MAX_RECENTS, MissionsState } from '../../missions/model/MissionsState.ts';
import { EditableMissionState } from '../../mission/model/EditableMissionState.ts';
import { RecentMissionItem } from './RecentMissionItem.tsx';
import { TrackingService } from '../../support/tracking/TrackingService.ts';
import { mediaLargerScreen } from '../../support/helpers/breakpoints.ts';
import { useBreakpoints } from '../../support/hooks/useBreakpoints.tsx';
import { SIDE_DRAWER_WIDTH } from '../../components/drawer/Drawer.tsx';
import { MENU_COLLAPSED_WIDTH } from '../../menu/Menu.tsx';
import { dayjs } from '../../support/helpers/dayjs.ts';
import { useMemo } from 'react';
import { Spinner } from '../../components/spinner/Spinner.tsx';

const useStyles = createUseStyles({
    modal: {
        [mediaLargerScreen]: {
            '--width': `${SIDE_DRAWER_WIDTH}px`,
            '--border-radius': '0px',
            width: `${SIDE_DRAWER_WIDTH}px`,
            left: MENU_COLLAPSED_WIDTH,
            right: 'unset',
            display: 'block',
            '--height': 'auto',
            '&::part(content)': {
                height: '100%'
            },
            '&::part(backdrop)': {
                display: 'none'
            }
        }
    },
    sectionHeader: {
        fontSize: 12,
        fontWeight: 600,
        textTransform: 'uppercase',
        color: 'var(--ion-color-medium)',
        padding: '16px 0 4px',
        letterSpacing: '0.5px'
    },
    list: {
        padding: '0 16px 60px'
    }
});

const splitByWeek = (missions: EditableMissionState[]) => {
    const startOfWeek = dayjs().startOf('week');
    const thisWeek: EditableMissionState[] = [];
    const older: EditableMissionState[] = [];

    for (const mission of missions) {
        if (dayjs(mission.getStartTime()).isAfter(startOfWeek)) {
            thisWeek.push(mission);
        } else {
            older.push(mission);
        }
    }

    return { thisWeek, older };
};

interface IRecentMissionsModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelectMission: (mission: EditableMissionState) => void;
}

export const RecentMissionsModal: React.FC<IRecentMissionsModalProps> = ({ isOpen, onClose, onSelectMission }) => {
    const classes = useStyles();

    const { isLargerScreen } = useBreakpoints();

    const tracker = useInstance(TrackingService);

    const missionsState = useInstance(MissionsState);
    useSubscription(() => missionsState);

    const recentMissions = useMemo(
        () => missionsState.getRecentMissions(),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [missionsState.state.lastLoaded]
    );

    const { thisWeek, older } = useMemo(
        () => splitByWeek(recentMissions),
        [recentMissions]
    );

    const isLoading = !missionsState.state.isFullyLoaded && recentMissions.length < MAX_RECENTS;
    const showSectionHeaders = thisWeek.length > 0 && older.length > 0;

    const handleSelect = (mission: EditableMissionState) => {
        tracker.track('Recent Mission Selected', () => ({ from: 'recents list', address: mission.getAddress() }));
        onClose();
        onSelectMission(mission);
    };

    return (
        <IonModal
            isOpen={isOpen}
            onDidDismiss={onClose}
            className={classes.modal}
            initialBreakpoint={isLargerScreen ? undefined : 0.95}
            breakpoints={isLargerScreen ? undefined : [0, 0.95]}
            handle={!isLargerScreen}
            data-id="recent-missions-modal"
        >
            <PageHeader title="Recents" onClose={onClose} />
            <IonContent>
                <div className={classes.list}>
                    {showSectionHeaders && <div className={classes.sectionHeader}>This Week</div>}
                    {thisWeek.map(m => (
                        <RecentMissionItem key={m.state.missionId} mission={m} onSelect={handleSelect} />
                    ))}

                    {showSectionHeaders && <div className={classes.sectionHeader}>Older</div>}
                    {older.map(m => (
                        <RecentMissionItem key={m.state.missionId} mission={m} onSelect={handleSelect} />
                    ))}

                    {isLoading && (
                        <div style={{textAlign: 'center', marginTop: 12}}>
                            <Spinner size="small" />
                        </div>
                    )}
                </div>
            </IonContent>
        </IonModal>
    );
};
